import React, {useEffect, useMemo, useState} from 'react';
import Row from "./Row";
import {schemas} from "../../config";
import styled from "styled-components";
import {useAppDispatch, useAppSelector} from "../../hooks";
import {Field, FieldHeadAlignU, PrimitiveObject, TableFieldSetI} from "../../Types";
import {fetchTableByQuery, tableInfoI} from "../../features/tableSlice";
import TableBottomPagination from "./TableBottomPagination";
import Loader from "../Loader";
import FakeRow from "./FakeRow";
import {shallowEqual} from "react-redux";
import {LANG} from "../CONSTANTS";


function selectFieldsForRendering(fields: Record<string, Field>, set: TableFieldSetI): Field[] {
    let res: Field[] = []

    let inCount = 0;
    set.fields.forEach(f => {
        if (f.key in fields) {
            const inline = (fields[f.key].inline || f.forceInline) && !f.forceNonInline;
            res.push({...fields[f.key], inline: inline})
            inline && inCount++
        }
    })
    if (inCount === 0 && res.length > 0)
        res[0].inline = true

    if (res.length > 1 && res[0].key !== "n")
        res.unshift({
            key: "n",
            inline: true,
            title: {ua: "№"}, type: "number",
            applyTransform: true
        })
    if (res.length === 0)
        res.unshift({
            key: "n",
            inline: true,
            title: {ua: "№"}, type: "number",
            applyTransform: true
        })
    return res
}

const Table = ({tableName, projectName, tableInfo}: { tableName: string, projectName: string, tableInfo: tableInfoI }) => {

    const tableSchema = schemas[projectName].tables[tableName];
    const fields = tableSchema.fields as Field[];
    const pkField = tableSchema.pk as string;
    const {inConditionRecords,  page, prevPage} = useAppSelector(state => ({
        inConditionRecords: state.tables.count[tableName].inCondition,
        page: state.tables.pages[tableName][tableInfo.current_page],
        prevPage: state.tables.pages[tableName][tableInfo.prev_page],
    }), shallowEqual);

    let isAdmin = useAppSelector(state => state.project.isUserAdmin);
    let isSideHeaderReady = useAppSelector(state => state.layout.sideHeaderReady);


    const dispatch = useAppDispatch();

    useEffect(() => {
        !page && tableInfo.loading === 'idle' && dispatch(fetchTableByQuery())
    }, [dispatch, page, tableInfo.loading]);


    const renderFields = useMemo(() => {
        return tableSchema.fieldSets?.sets[tableInfo.selectedFieldSet]
            ? selectFieldsForRendering(
                Object.fromEntries(fields.map(f => [f.key, f])),
                tableSchema.fieldSets.sets[tableInfo.selectedFieldSet]
            )
            : fields;
    }, [fields, tableSchema.fieldSets, tableInfo.selectedFieldSet]);

    const cols = useMemo(() => renderFields.filter(x => x.inline).length, [renderFields]);

    const renderPage = page ?? prevPage;

    return (
        <TableWrapper>
            <table className="custom-table">
                <thead>
                <tr>
                    {renderFields.map(field => !field.inline ? null :
                        <HeaderCell key={field.key} $align={field.headAlign || "center"}>
                            {field.shortHead?.[LANG] || field.title[LANG]}
                        </HeaderCell>)}
                </tr>
                </thead>
                <tbody>
                {renderPage && renderPage.map((record: any, i: number) =>
                    <Row record={record} schema={renderFields} cols={cols}
                         key={i}
                         isAdmin={isAdmin}
                         isSideHeaderReady={!!isSideHeaderReady}
                         pkField={pkField} tableName={tableName}/>)}
                {renderPage && renderPage.length === 0 && <tr className={'not-found-table-row'}>
                    <td colSpan={cols} className={'text-align-center not-found-table-cell'}>Записів що підпадають під
                                                                                            умови не знайдено
                    </td>
                </tr>}
                {!renderPage && Array.from({length: 20}, (_, index) => (
                    <FakeRow key={index} schema={renderFields}/>
                ))}
                </tbody>
                <tfoot>
                <tr>
                    <TableBottomPagination maxPages={tableInfo.max_pages} currentPage={tableInfo.current_page}
                                           tableName={tableName}
                                           inConditionRecords={inConditionRecords} cols={cols}/>
                </tr>
                </tfoot>
            </table>
            {tableInfo.loading === 'loading' && <LoaderContainer>
                <Loader/>
            </LoaderContainer>}


        </TableWrapper>
    );
};

export default Table;

export const HeaderCell = styled.th<{ $align: FieldHeadAlignU }>`
    text-align: ${({$align}) => $align};
`

export const LoaderContainer = styled.div`

    background: rgba(0, 0, 0, 0.54);

    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
`

const TableWrapper = styled.div`
    overflow-x: auto;
    max-width: 100vw;
    position: relative;

    & .custom-table {
        width: 100%;
        //border-collapse: collapse;
        text-align: left;
        color: var(--table-row-text-color);
        //border: 1px solid var(--table-border-color);


        & > thead {
            background-color: var(--table-header-bg-color);
            color: var(--table-header-text-color);
            font-weight: bold;
            border-bottom: 1px solid var(--table-border-color);
            border-top: 1px solid var(--table-border-color);
        }

        & > tfoot {
            border-top: 1px solid var(--table-border-color);
            background-color: var(--table-footer-bg-color);
            color: var(--table-footer-text-color);
        }

        th {
            padding: 6px;
            white-space: nowrap;
        }

        td {
            //padding: 12px;
            padding: var(--td-padding);
            //border: 2px solid var(--table-border-color);
        }

        tbody {
            > tr:nth-child(even) {
                background-color: var(--table-row-even-bg-color);
            }

            > tr:nth-child(odd) {
                background-color: var(--table-row-odd-bg-color);
            }
        }
    }

    .center-td {
        text-align: center;
    }

    .edit-table {
        width: 100%;
        border-radius: 4px;
        border-collapse: collapse;
    }

    .edit-textarea, .edit-input-number, .edit-input-text {
        width: 100%;
        text-indent: 0;
    }
`;
